import { Pagination } from '@mui/lab';
import { Box, Button, Grid, LinearProgress, SxProps, Theme, Typography } from '@mui/material';
import { QuoteCard } from 'quotes/quote-card';
import { QuoteSearch } from 'quotes/quote-search';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { BRANDS } from 'shared/constants';
import { useQuoteListState } from 'shared/contexts';
import { useDebounce, useTitle } from 'shared/hooks';
import { useQueryQuotes } from 'shared/queries';
import { Brands, UserInfo } from 'shared/types';

const rootStyles: SxProps<Theme> = theme => ({
  height: '100%',
  width: '100%',
  padding: theme.spacing(2),
});
const loadingBarStyles: SxProps<Theme> = {
  height: 4,
};

interface QuotesProps {
  brand: Brands;
  user: UserInfo;
}

export function Quotes({ brand, user }: QuotesProps) {
  const history = useHistory();
  const { state, setPage, saveState } = useQuoteListState();
  const debouncedState = useDebounce(state, 300);
  const { quotes, isFetching, isLoading } = useQueryQuotes({
    ...state,
    brand,
    pageSize: 9,
    salesRep: user.displayName,
    search: debouncedState?.search,
  });

  useEffect(() => {
    if (saveState) {
      saveState(debouncedState);
    }
  }, [debouncedState, saveState]);

  useTitle(`${BRANDS[brand]} - Quotes`);
  const navigateToNewQuote = useCallback(() => {
    history.push(`${history.location.pathname}/new`);
  }, [history]);

  return (
    <>
      <Box sx={loadingBarStyles}>{(isFetching || isLoading) && <LinearProgress />}</Box>
      <Grid container direction='column' spacing={2} sx={rootStyles}>
        <Grid item xs container justifyContent='space-between'>
          <Grid item>
            <Typography variant='h5'>{quotes?.totalCount || 0} Quotes</Typography>
          </Grid>
          <Grid item>
            <Button variant='contained' color='secondary' onClick={navigateToNewQuote}>
              Add New Quote
            </Button>
          </Grid>
        </Grid>
        <Grid item xs>
          <QuoteSearch totalCount={quotes?.totalCount || 0} />
        </Grid>
        <Grid item xs container spacing={1} justifyContent='center' alignItems='stretch'>
          {(quotes?.data || []).map(quote => (
            <Grid item xs={12} sm={12} md={6} lg={4} xl={4} key={quote.id}>
              <QuoteCard quote={quote} user={user} />
            </Grid>
          ))}
        </Grid>
        <Grid item container xs justifyContent='center'>
          {Boolean(quotes?.data.length) && (
            <Pagination
              count={quotes?.totalPages}
              page={state.page}
              onChange={(_, page) => {
                setPage(page);
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}
